import React, { useState, useEffect, useRef, useCallback } from "react"

import {
	DispatchProductDetailPageReducer,
	setInitialConfigurations,
	ProductDetailPageReducerState,
	setSingleConfigurationGroupConfiguration,
	setMultiConfigurationGroupConfiguration,
} from "@secureo/common/reducers/productDetailPageReducer"

import FloatingPrice from "containers/ProductDetailPage/PriceComponents/FloatingPrice"
import ConfigurationGroups from "./ConfigurationGroups"
import { ConfigurationGroupProductsBySku } from "@secureo/common/typings/Product"

const ConfigurationGroupsWithFloatingPrice = ({
	accessToken,
	dispatch,
	reducerState,
	addToCart,
	addToShoppingList,
	setQuantity,
}: Props) => {
	const [showDesktopPriceComponent, setShowDesktopPriceComponent] = useState(false)

	const {
		isLoading,
		attributes: { configurationGroups = [], manufacturer },
		configurationsByConfigurationGroup,
		taxRate,
		countryCode,
		quantity,
		singleGrossPriceCents,
		singleGrossMsrpPriceCents,
	} = reducerState

	const ref = useRef(null)
	const handleScroll = useCallback(() => {
		if (ref?.current) {
			const topOffset = ref?.current?.getBoundingClientRect()?.top
			const bottomOffset = ref?.current?.getBoundingClientRect()?.bottom

			if (!showDesktopPriceComponent && topOffset < 100) {
				setShowDesktopPriceComponent(true)
			}
			if (showDesktopPriceComponent && topOffset > 100) {
				setShowDesktopPriceComponent(false)
			}
			if (bottomOffset < 160) {
				setShowDesktopPriceComponent(false)
			}
		}
	}, [showDesktopPriceComponent])

	useEffect(() => {
		window.addEventListener("scroll", handleScroll)
		return () => {
			window.removeEventListener("scroll", handleScroll)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleScroll])

	const onConfigurationGroupsFetched = (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
	) => {
		setInitialConfigurations(dispatch, reducerState, configurationGroupProductsBySku)
	}

	const onSingleConfigurationGroupConfigurationSet = (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
	) => {
		setSingleConfigurationGroupConfiguration(
			dispatch,
			reducerState,
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
		)
	}

	const onMultiConfigurationGroupConfigurationSet = (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
		quantity: number,
	) => {
		setMultiConfigurationGroupConfiguration(
			dispatch,
			reducerState,
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
			quantity,
		)
	}

	return (
		<div ref={ref}>
			<ConfigurationGroups
				accessToken={accessToken}
				configurationGroups={configurationGroups}
				configurationsByConfigurationGroup={configurationsByConfigurationGroup}
				taxRate={taxRate}
				countryCode={countryCode}
				onConfigurationGroupsFetched={onConfigurationGroupsFetched}
				setSingleConfigurationGroupConfiguration={
					onSingleConfigurationGroupConfigurationSet
				}
				setMultiConfigurationGroupConfiguration={onMultiConfigurationGroupConfigurationSet}
			/>

			{showDesktopPriceComponent && (
				<div style={{ position: "fixed", top: 0 }}>
					<FloatingPrice
						singleGrossPriceCents={singleGrossPriceCents}
						singleGrossMsrpPriceCents={singleGrossMsrpPriceCents}
						addToCart={addToCart}
						addToShoppingList={addToShoppingList}
						isLoading={isLoading}
						quantity={quantity}
						setQuantity={setQuantity}
						variant="configuration"
						manufacturer={manufacturer}
					/>
				</div>
			)}
		</div>
	)
}

interface Props {
	accessToken: string
	dispatch: DispatchProductDetailPageReducer
	reducerState: ProductDetailPageReducerState
	setQuantity: (quantity: number) => void
	addToCart: () => void
	addToShoppingList: () => void
}

export default ConfigurationGroupsWithFloatingPrice
